import * as Yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Stack,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FormProvider, { RHFTextField } from "../../../components/hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import {
  getProfile,
  updateUserEmailOrPhone,
} from "src/redux/slices/loginSlice";
import { useSelector } from "react-redux";
import { useSnackbar } from "../../../components/snackbar";
import Iconify from "src/components/iconify/Iconify";
import VerificationDialog from "./VerificationDialog";

const CountryCode = process.env.REACT_APP_COUNTRY_CODE;

export default function PhoneVerifyInReservation({ context }) {
  const { user } = useSelector((state) => state.login);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  useEffect(() => {
    if (user?.id) {
      dispatch(getProfile(user?.id));
    }
  }, [dispatch, user?.id]);
  const RegisterSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/,
        "Invalid Email Format"
      ),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  });

  const defaultValues = {
    email: user?.email || "",
    phone: user?.phone || "",
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    control,
    formState: { errors},
  } = methods;

  const navigate = useNavigate();

  let userData  
  let [phoneNumberForModal,setphoneNumberForModal]=useState('')
  
  const onSubmit = async (data) => {
      try {
         userData = {
          isPhone: "true",
          ...data,
        };
        setphoneNumberForModal(data?.phone)
        await dispatch(updateUserEmailOrPhone(userData, user?.id));
        setOpenModal(true);
      } catch (error) {
        setErrorMsg(error?.response?.data?.message);
      }
    
  };

  const handleModalClose = () => setOpenModal(false);
  return (
    <>
      <Box>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center">
              <RHFTextField
                name="email"
                label="Email"
                disabled={!!user?.email}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <Iconify
                          icon={
                            user?.isEmailVerified
                              ? "eva:checkmark-circle-fill"
                              : "eva:clock-outline"
                          }
                          style={{
                            width: 20,
                            height: 20,
                            color: user?.isEmailVerified
                              ? "#36B37E"
                              : "#FFAB00",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Stack sx={{ display: "flex", flexDirection: "row", gap: "5px" }}>
              <RHFTextField
                sx={{ borderRadius: "transparent" }}
                name="phone"
                label="Phone number"
                placeholder="3xxxxxxxxx"
                error={Boolean(errors.phone) || Boolean(errorMsg)}
                disabled={!!user?.isPhoneVerified}
                helperText={
                  errors.phone ? errors.phone.message : errorMsg || ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span>{CountryCode}</span>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <Iconify
                          icon={
                            user?.isPhoneVerified
                              ? "eva:checkmark-circle-fill"
                              : "eva:clock-outline"
                          }
                          style={{
                            width: 20,
                            height: 20,
                            color: user?.isPhoneVerified
                              ? "#36B37E"
                              : "#FFAB00",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                  inputMode: "numeric",
                }}
                inputProps={{
                  maxLength: 10,
                  onInput: (e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                    setErrorMsg("");
                  },
                  onKeyDown: (e) => {
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                  },
                }}
              />
            </Stack>
          </Stack>
         {!user?.isPhoneVerified && <Stack sx={{ display: "flex", flexDirection: "row-reverse" }}>
            <LoadingButton
              color="inherit"
              type="submit"
              variant="contained"
              sx={{
                width: "20%",
                marginTop: "15px",
                bgcolor: "#FCA92E",
                color: (theme) =>
                  theme.palette.mode === "light" ? "#000000" : "grey.800",
                "&:hover": {
                  bgcolor: "text.primary",
                  color: (theme) =>
                    theme.palette.mode === "light"
                      ? "common.white"
                      : "grey.800",
                },
              }}
            >
              Update
            </LoadingButton>
          </Stack>}
        </FormProvider>
        {context === "ReservationCheckoutSummary" && (
          <VerificationDialog open={openModal} onClose={handleModalClose} phoneNumberForModal={phoneNumberForModal}/>
        )}
      </Box>
    </>
  );
}
